import { getPdfPageCount } from './pdfCompressor';

const API_URL = '/api/v1';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Request failed');
    }

    return data;
  }

  // Auth endpoints
  async register(email: string, password: string, name?: string) {
    return this.request<{
      user: User;
      access_token: string;
      refresh_token: string;
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
  }

  async login(email: string, password: string) {
    return this.request<{
      user: User;
      access_token: string;
      refresh_token: string;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async logout(refreshToken: string) {
    return this.request('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  }

  // Password reset endpoints
  async requestPasswordReset(email: string) {
    return this.request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string) {
    return this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // Email verification endpoints
  async verifyEmail(token: string) {
    return this.request<{ message: string }>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerificationEmail() {
    return this.request<{ message: string }>('/auth/resend-verification', {
      method: 'POST',
    });
  }

  // User endpoints
  async getProfile() {
    return this.request<{ user: User; subscription: Subscription; usage: Usage }>('/users/me');
  }

  async updateProfile(data: { name?: string; organization?: string }) {
    return this.request<{ user: User }>('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Document endpoints
  async getDocuments(params?: { page?: number; limit?: number; status?: string }) {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request<{ documents: Document[] }>(`/documents${query ? `?${query}` : ''}`);
  }

  async getDocument(id: string) {
    return this.request<{ document: Document; validation_report: ValidationReport; certificate: Certificate }>(`/documents/${id}`);
  }

  async deleteDocument(id: string) {
    return this.request(`/documents/${id}`, {
      method: 'DELETE',
    });
  }

  async reprocessDocument(id: string) {
    return this.request(`/documents/${id}/reprocess`, {
      method: 'POST',
    });
  }

  async repairDocument(id: string) {
    return this.request(`/documents/${id}/repair`, {
      method: 'POST',
    });
  }

  // ============================================================
  // Server-Side Remediation
  // ============================================================

  /**
   * Maps internal step names to user-friendly messages.
   */
  private friendlyStep(step: string, progress: number): string {
    const s = (step || '').toLowerCase();
    if (progress <= 5 || s.includes('load'))                         return 'Preparing your document';
    if (progress <= 25 || s.includes('render'))                      return 'Analysing document structure';
    if (progress <= 70 || s.includes('ai') || s.includes('analy'))   return 'Applying accessibility improvements';
    if (progress <= 95 || s.includes('tag') || s.includes('inject') || s.includes('final')) return 'Generating accessible PDF';
    if (s.includes('complete'))                                      return 'Finishing up';
    return 'Processing your document';
  }

  async v8Remediate(
    file: File,
    onProgress?: (status: string) => void,
    _onLog?: (log: string) => void,
    onJobStarted?: (jobId: string, reservationId?: string, estimatedPages?: number) => void | Promise<void>
  ): Promise<{
    success: boolean;
    blob?: Blob;
    jobId?: string;
    reservationId?: string;
    processingTimeMs?: number;
    pageCount?: number;
    stats?: any;
    error?: string;
    r2Key?: string;
  }> {
    const startTime = Date.now();
    let activeJobId: string | undefined;
    let activeReservationId: string | undefined;

    try {
      // Step 1: Get ticket from Cloudflare
      onProgress?.('Preparing your document...');
      const estimatedPages = await getPdfPageCount(file);
      if (!estimatedPages) {
        return { success: false, error: 'Could not read the PDF page count. Please check the file and try again.' };
      }

      const ticketHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = this.getToken();
      if (token) {
        ticketHeaders['Authorization'] = `Bearer ${token}`;
      }
      const ticketResponse = await fetch('/api/v8/ticket', {
        method: 'POST',
        headers: ticketHeaders,
        body: JSON.stringify({
          filename: file.name,
          estimatedPages,
        }),
      });

      if (!ticketResponse.ok) {
        const err = await ticketResponse.json().catch(() => ({ error: { code: 'NETWORK_ERROR', message: 'Could not reach the processing service. Please check your connection and try again.' } }));
        const errorCode = (err as any).error?.code;
        if (errorCode === 'PAGE_CREDITS_EXHAUSTED') {
          return { success: false, error: 'PAGE_CREDITS_EXHAUSTED' };
        }
        if (errorCode === 'RATE_LIMITED') {
          return { success: false, error: 'Too many requests. Please wait a moment and try again.' };
        }
        if (errorCode === 'SERVICE_UNAVAILABLE' || errorCode === 'TICKET_FAILED' || ticketResponse.status === 502 || ticketResponse.status === 503) {
          return { success: false, error: 'The processing service is temporarily unavailable. Please try again in a few minutes.' };
        }
        return { success: false, error: (err as any).error?.message || 'Failed to prepare your document. Please try again.' };
      }

      const ticketResult = await ticketResponse.json() as any;
      const ticket = ticketResult.data?.ticket || ticketResult.ticket;
      const v8Url = ticketResult.data?.v8Url || ticketResult.v8Url || '';
      const reservationId = ticketResult.data?.reservationId || ticketResult.reservationId;
      activeReservationId = reservationId;

      if (!ticket) {
        return { success: false, error: 'No ticket received from server' };
      }

      const baseUrl = v8Url || '';

      // Step 2: Upload PDF
      onProgress?.(`Uploading ${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)...`);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('ticket', ticket);
      formData.append('scale', '1.2');
      formData.append('quality', '85');

      const submitUrl = baseUrl ? `${baseUrl}/api/v8/submit` : '/api/v8/submit';
      let submitResponse;
      let submitAttempts = 0;
      const maxSubmitAttempts = 4; // 1 initial + 3 retries
      let lastErrMsg = null;

      while (submitAttempts < maxSubmitAttempts) {
        submitAttempts++;
        submitResponse = await fetch(submitUrl, {
          method: 'POST',
          body: formData,
        });

        if (submitResponse.ok || submitResponse.status === 400 || submitResponse.status === 401 || submitResponse.status === 413) {
          // Break on success or permanent client errors
          break;
        }

        // Handle retryable errors (429, 502, 503, 504)
        const err = await submitResponse.json().catch(() => null);
        lastErrMsg = (err as any)?.error?.message || (err as any)?.error;
        
        if (submitAttempts < maxSubmitAttempts) {
          const backoffSecs = Math.pow(2, submitAttempts); // 2s, 4s, 8s
          onProgress?.(`Service busy, queuing document... (retrying in ${backoffSecs}s)`);
          await new Promise(resolve => setTimeout(resolve, backoffSecs * 1000));
        }
      }

      if (!submitResponse || !submitResponse.ok) {
        if (submitResponse?.status === 413) {
          return { success: false, error: 'File is too large. Please try a smaller PDF (max 24MB).', reservationId: activeReservationId };
        }
        if (submitResponse?.status === 401) {
          return { success: false, error: 'Your session has expired. Please refresh the page and try again.', reservationId: activeReservationId };
        }
        if (submitResponse?.status === 402 || submitResponse?.status === 403) {
          return { success: false, error: 'PAGE_CREDITS_EXHAUSTED', reservationId: activeReservationId };
        }
        if (submitResponse?.status === 502 || submitResponse?.status === 503) {
          return { success: false, error: 'The processing service is temporarily unavailable. Please try again in a few minutes.' };
        }
        const displayMsg = typeof lastErrMsg === 'string' ? lastErrMsg : null;
        return { success: false, error: displayMsg || `Upload failed (${submitResponse?.status || 'Unknown'}). Please try again.`, reservationId: activeReservationId };
      }

      const submitData = await submitResponse.json() as any;
      const jobId = submitData.jobId;
      const jobToken = submitData.jobToken;

      if (!jobId) {
        return { success: false, error: 'No job ID received from server', reservationId: activeReservationId };
      }

      activeJobId = jobId;

      // Notify caller that the job has started (so they can create a processing record)
      await onJobStarted?.(jobId, activeReservationId, estimatedPages);

      onProgress?.(`Processing your document... (Job ID: ${jobId})`);

      // Step 3: Poll for completion
      const pollUrl = baseUrl ? `${baseUrl}/api/v8/job/${jobId}` : `/api/v8/job/${jobId}`;
      const maxAttempts = 600; // 30 minutes max (3s intervals = 30 min)
      let attempt = 0;
      let lastProgress = 0;
      let total404s = 0;
      const maxTotal404s = 60; // hard limit (~3 min of pure 404s)
      let lastSuccessfulPoll = Date.now();

      while (attempt < maxAttempts) {
        attempt++;
        await new Promise(resolve => setTimeout(resolve, attempt <= 3 ? 1000 : 3000));

        try {
          const pollResponse = await fetch(pollUrl, {
            headers: jobToken ? { 'X-Job-Token': jobToken } : undefined,
          });

          if (!pollResponse.ok) {
            if (pollResponse.status === 404) {
              total404s++;

              // Show reassuring messages while waiting for the job to appear
              const waitingSecs = Math.floor((Date.now() - lastSuccessfulPoll) / 1000);
              if (waitingSecs < 30) {
                onProgress?.('Processing your document...');
              } else if (waitingSecs < 90) {
                onProgress?.('Still processing — this may take a moment for larger documents...');
              } else {
                onProgress?.('Processing is taking longer than expected — please wait...');
              }

              if (total404s >= maxTotal404s) {
                // Last resort: check if R2 has the result (via the Cloudflare R2 download endpoint)
                try {
                  const r2Check = await fetch(`/api/v8/r2/${jobId}/download`, {
                    method: 'HEAD',
                    headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
                  }); // fast-check doesn't have r2Key yet
                  if (r2Check.ok) {
                    // R2 has the file — proceed to download
                    onProgress?.('Downloading remediated PDF...');
                    const r2Download = await fetch(`/api/v8/r2/${jobId}/download`, {
                      headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
                    });
                    if (r2Download.ok) {
                      const blob = await r2Download.blob();
                      const processingTimeMs = Date.now() - startTime;
                      onProgress?.(`Complete! (${(processingTimeMs / 1000).toFixed(1)}s)`);
                      // Page count unknown from R2-only fallback — the backend
                      // save-result handler will attempt to read it from stats or
                      // the actual PDF. Pass 0 here; server side will reconcile.
                      return { success: true, blob, jobId, reservationId: activeReservationId, processingTimeMs, pageCount: 0, stats: { pages: 0 } };
                    }
                  }
                } catch { /* R2 check failed — fall through to error */ }

                return { success: false, error: 'Your document could not be located for processing. No page credits were deducted. Please try again.', jobId, reservationId: activeReservationId };
              }
              continue;
            }
            // Non-404 error: tolerate a few before giving up
            if (attempt > 20) {
              return { success: false, error: 'Processing encountered an issue. No page credits were deducted. Please try again.', jobId, reservationId: activeReservationId };
            }
            continue;
          }

          // Successful poll — update tracking
          total404s = Math.max(0, total404s - 2); // Slowly recover 404 budget on success
          lastSuccessfulPoll = Date.now();
          const pollData = await pollResponse.json() as any;

          // Update progress with user-friendly step names (no internal pipeline details)
          if (pollData.progress !== undefined && pollData.progress > lastProgress) {
            lastProgress = pollData.progress;
            const friendly = this.friendlyStep(pollData.step || '', pollData.progress);
            onProgress?.(`${friendly} (${pollData.progress}%)`);
          }

          // Engine logs are internal only — not forwarded to the UI.

          if (pollData.status === 'completed') {
            onProgress?.('Downloading remediated PDF...');

            // Step 4: Download result
            // Primary: Fetch directly from Cloudflare Worker/R2 to bypass Cloud Run's 32MB response limit
            const r2DownloadUrl = pollData.r2Key ? `/api/v8/r2/${jobId}/download?key=${encodeURIComponent(pollData.r2Key)}` : `/api/v8/r2/${jobId}/download`;
            let downloadResponse = await fetch(r2DownloadUrl, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
            });

            // Fallback: If not found in R2, try fetching from the Cloud Run engine's memory
            if (!downloadResponse.ok) {
              onProgress?.('Retrieving your document from memory fallback...');
              const engineDownloadUrl = baseUrl
                ? `${baseUrl}/api/v8/job/${jobId}/download`
                : `/api/v8/job/${jobId}/download`;
              downloadResponse = await fetch(engineDownloadUrl, {
                headers: jobToken ? { 'X-Job-Token': jobToken } : undefined,
              });
            }

            if (!downloadResponse.ok) {
              return { success: false, error: 'Download failed. No page credits were deducted. Please try again.', jobId, reservationId: activeReservationId };
            }

            const blob = await downloadResponse.blob();
            const processingTimeMs = Date.now() - startTime;

            onProgress?.(`Complete! (${(processingTimeMs / 1000).toFixed(1)}s)`);

            return {
              success: true,
              blob,
              jobId,
              reservationId: activeReservationId,
              processingTimeMs,
              pageCount: pollData.stats?.pages || pollData.pageCount || 0,
              stats: pollData.stats,
              r2Key: pollData.r2Key,
            };
          }

          if (pollData.status === 'failed' || pollData.status === 'error') {
            // V8 failure: return graceful error. No credits are settled for failed jobs.
            const errorMsg = pollData.error || 'Processing failed';
            return {
              success: false,
              error: `${errorMsg}. No page credits were deducted. Please try resubmitting your document.`,
              jobId,
              reservationId: activeReservationId,
            };
          }
        } catch {
          // Network error during poll — retry silently for a while
          if (attempt > 25) {
            return {
              success: false,
              error: 'A network issue interrupted processing. No page credits were deducted. Please try again.',
              jobId,
              reservationId: activeReservationId,
            };
          }
        }
      }

      // Timeout: no credits deducted since save-result is never called
      return { success: false, error: 'Processing timed out. No page credits were deducted. Please try again with a smaller document or contact support.', jobId: activeJobId, reservationId: activeReservationId };
    } catch {
      // Network/unexpected failure: no credits deducted since save-result is never called
      return {
        success: false,
        error: 'Something went wrong. No page credits were deducted. Please try again.',
        jobId: activeJobId,
        reservationId: activeReservationId,
      };
    }
  }

  /**
   * Save V8 remediation result to D1 database.
   * Called after successful V8 remediation to create a document record,
   * update usage tracking, and link the result to the user's account.
   * 
   * Only works for authenticated users -- anonymous users get a silent skip.
   */
  async saveV8Result(data: {
    filename: string;
    fileSize: number;
    pageCount: number;
    processingTimeMs: number;
    jobId?: string;
    reservationId?: string;
    estimatedPages?: number;
    stats?: Record<string, unknown>;
    status?: 'processing' | 'completed' | 'failed';
    r2Key?: string;
  }): Promise<{ success: boolean; documentId?: string; error?: string }> {
    try {
      const token = this.getToken();
      if (!token) {
        // Not authenticated -- skip silently (anonymous user)
        return { success: true };
      }

      const response = await fetch('/api/v8/save-result', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: { message: 'Save failed' } }));
        const errorMsg = (err as any).error?.message || `Save failed (HTTP ${response.status})`;
        console.error('[saveV8Result] Failed:', response.status, errorMsg);
        return { success: false, error: errorMsg };
      }

      const result = await response.json() as any;
      console.log('[saveV8Result] Success:', result.data?.documentId);
      return {
        success: true,
        documentId: result.data?.documentId,
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Save failed';
      console.error('[saveV8Result] Exception:', errorMsg);
      return { success: false, error: errorMsg };
    }
  }

  async pollDocumentStatus(ids: string) {
    return this.request<{ 
      documents: Array<{
        id: string;
        status: string;
        error_message: string | null;
        page_count: number | null;
        processing_completed_at: string | null;
        certificate_id: string | null;
        verification_code: string | null;
        compliance_score: number | null;
      }>;
      timestamp: number;
    }>(`/documents/status/poll?ids=${encodeURIComponent(ids)}`);
  }

  // Payment endpoints
  async getPacks() {
    return this.request<{ packs: PagePack[] }>('/payments/packs');
  }

  async getBalance() {
    return this.request<{ balance: PageBalance; purchases: PagePackPurchase[] }>('/payments/balance');
  }

  async createCheckout(pack_id: string) {
    return this.request<{ checkout_url: string }>('/payments/checkout', {
      method: 'POST',
      body: JSON.stringify({ pack_id }),
    });
  }

  /**
   * Create a Stripe checkout session for a custom number of page credits at $2/page.
   * This is the simplified launch pricing endpoint.
   */
  async createPageCheckout(pages: number) {
    return this.request<{
      checkout_url: string;
      session_id: string;
      pages: number;
      total_cents: number;
      total_display: string;
    }>('/payments/checkout-pages', {
      method: 'POST',
      body: JSON.stringify({ pages }),
    });
  }

  async getPortalUrl() {
    return this.request<{ portal_url: string }>('/payments/portal');
  }

  // Certificate endpoints
  async getCertificates() {
    return this.request<{ certificates: Certificate[] }>('/certificates');
  }

  async verifyCertificate(code: string) {
    return this.request<{ valid: boolean; certificate: Certificate }>(`/certificates/verify/${code}`);
  }

  // Billing endpoints
  async getInvoices() {
    return this.request<{ invoices: Invoice[] }>('/payments/invoices');
  }

  async getPaymentMethod() {
    return this.request<{ payment_method: PaymentMethod | null }>('/payments/payment-method');
  }

  /**
   * Redeem an Enterprise Trial code for included page credits.
   */
  async redeemTrialCode(code: string) {
    return this.request<{
      pages_granted: number;
      trial_name: string;
      expires_at: string;
      message: string;
    }>('/payments/redeem-trial', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  /**
   * Redeem enterprise campaign bonus (triggered by login via campaign email link).
   * No code needed — the tracked ref parameter is the proof of provenance.
   */
  async redeemEnterpriseCampaign() {
    return this.request<{
      pages_granted: number;
      already_redeemed: boolean;
      message: string;
    }>('/payments/redeem-enterprise-campaign', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }


}

// Types
export interface User {
  id: string;
  email: string;
  name: string | null;
  organization: string | null;
  plan: 'free' | 'professional' | 'enterprise';
  email_verified: boolean;
  created_at: string;
}

export interface Subscription {
  plan: string;
  status: string;
  pages_used: number;
  pages_limit: number | null;
  current_period_start: string | null;
  current_period_end: string | null;
}

export interface Usage {
  total_documents: number;
  completed_documents: number;
  processing_documents: number;
}

export interface Document {
  id: string;
  original_filename: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  page_count: number | null;
  file_size: number | null;
  processing_started_at: string | null;
  processing_completed_at: string | null;
  error_message: string | null;
  created_at: string;
  has_remediated?: boolean;
}

export interface ValidationReport {
  compliance_score: number;
  pdfua_compliant: boolean;
  wcag_aa_compliant: boolean;
  issues_critical: number;
  issues_major: number;
  issues_minor: number;
  created_at: string;
}

export interface Certificate {
  id: string;
  document_id: string;
  verification_code: string;
  standards_met: string;
  issued_at: string;
  expires_at: string | null;
}

export interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  interval: string | null;
  features: string[];
  limits: {
    pages: number;
    max_file_size: string;
    validity_months: number;
  };
}

export interface PagePack {
  id: string;
  name: string;
  pages: number;
  price: number;
  pricePerPage: number;
  description: string;
  features: string[];
  validityMonths?: number;
  badge?: string;
}

export interface PageBalance {
  pages_remaining: number;
  pages_used: number;
  pages_limit: number;
  active_packs: number;
}

export interface PagePackPurchase {
  id: string;
  pack_id: string;
  pack_name: string;
  pages_purchased: number;
  pages_remaining: number;
  amount_paid: number;
  expires_at: string | null;
  created_at: string;
}

export interface Invoice {
  id: string;
  number: string | null;
  description: string | null;
  amount: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void' | 'pending';
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  created_at: string;
}

export interface PaymentMethod {
  id: string;
  type: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export const api = new ApiClient();
